import { RefObject, useEffect, useState } from 'react';

const PORTAL_SELECTOR = `.table-arrows-portal`;

export const ARROW_ZONE_WIDTH_PX = 48;

interface ArrowZoneRect {
  height: number;
  left: number;
  top: number;
}

interface TableArrowsPortalGeometry {
  left: ArrowZoneRect | null;
  portalNode: Element;
  right: ArrowZoneRect | null;
}

function findFarthestMatchingAncestor(
  element: Element,
  selector: string,
): Element | null {
  let farthestMatch: Element | null = null;
  let current: Element | null = element.closest(selector);

  while (current) {
    farthestMatch = current;
    current = current.parentElement?.closest(selector) ?? null;
  }

  return farthestMatch;
}

function computeZoneRect(
  regionTop: number,
  regionBottom: number,
  portalRect: DOMRect,
  zoneLeft: number,
): ArrowZoneRect | null {
  const visibleTop = Math.max(regionTop, portalRect.top);
  const visibleBottom = Math.min(regionBottom, portalRect.bottom);

  if (visibleBottom <= visibleTop) {
    return null;
  }

  return {
    height: visibleBottom - visibleTop,
    left: zoneLeft,
    top: visibleTop - portalRect.top,
  };
}

/**
 * While `isActive`, locates the nearest ancestor matching
 * `.table-arrows-portal` (if any) and computes, in coordinates local to that
 * portal, a left/right "arrow zone" rect (left, top, height) covering the
 * table's own visible height clipped to the portal's bounds. Consumers can
 * render a full-size overlay (inset: 0) into the portal and, for each zone,
 * an absolutely positioned strip sized to `height` with its content
 * vertically centered via flexbox — always aligned to the table regardless
 * of how tall it is. The left zone starts below the frozen header row.
 */
export function useTableArrowsPortal(
  frameRef: RefObject<HTMLElement | null>,
  headerRef: RefObject<HTMLElement | null>,
  isActive: boolean,
) {
  const [geometry, setGeometry] = useState<TableArrowsPortalGeometry | null>(
    null,
  );

  useEffect(() => {
    if (!isActive) {
      setGeometry(null);
      return undefined;
    }

    const frameEl = frameRef.current;
    const portalNode = frameEl
      ? findFarthestMatchingAncestor(frameEl, PORTAL_SELECTOR)
      : null;

    if (!frameEl || !portalNode) {
      setGeometry(null);
      return undefined;
    }

    let animationFrameId: number | null = null;

    const update = () => {
      animationFrameId = null;
      const frameRect = frameEl.getBoundingClientRect();
      const portalRect = portalNode.getBoundingClientRect();
      const headerHeight =
        headerRef.current?.getBoundingClientRect().height ?? 0;

      setGeometry({
        left: computeZoneRect(
          frameRect.top + headerHeight,
          frameRect.bottom,
          portalRect,
          frameRect.left - portalRect.left,
        ),
        portalNode,
        right: computeZoneRect(
          frameRect.top,
          frameRect.bottom,
          portalRect,
          frameRect.right - portalRect.left - ARROW_ZONE_WIDTH_PX,
        ),
      });
    };

    const scheduleUpdate = () => {
      if (animationFrameId !== null) {
        return;
      }
      animationFrameId = requestAnimationFrame(update);
    };

    update();
    window.addEventListener(`scroll`, scheduleUpdate, {
      capture: true,
      passive: true,
    });
    window.addEventListener(`resize`, scheduleUpdate);

    const resizeObserver = new ResizeObserver(scheduleUpdate);
    resizeObserver.observe(frameEl);
    resizeObserver.observe(portalNode);
    if (headerRef.current) {
      resizeObserver.observe(headerRef.current);
    }

    return () => {
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
      }
      window.removeEventListener(`scroll`, scheduleUpdate, true);
      window.removeEventListener(`resize`, scheduleUpdate);
      resizeObserver.disconnect();
    };
  }, [frameRef, headerRef, isActive]);

  return geometry;
}
